// src/pages/Profile/Profile.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../src/Hooks/useAuth';
import { API_BASE_URL, STORAGE_KEYS } from '../../src/helpers/Constant';

const Profile = () => {
  const navigate = useNavigate();
  const { user, token, updateUserData, logout, isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    username: '',
    address: '',
    image: null
  });
  const [previewImage, setPreviewImage] = useState('');
  const [orders, setOrders] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated && !user) {
      navigate('/signin');
    }
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    if (user) {
      // Split name into first and last name
      const nameParts = user.name?.split(' ') || [];
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      setFormData({
        first_name: firstName,
        last_name: lastName,
        email: user.email || '',
        phone: user.phone || '',
        username: user.username || '',
        address: user.address || '',
        image: null
      });
      setPreviewImage(user.image || 'https://jagods.com/backend/images/default/user.png');
    }
  }, [user]);

  useEffect(() => {
    if (activeTab === 'orders' && isAuthenticated) {
      fetchOrders();
    } else if (activeTab === 'reservations' && isAuthenticated) {
      fetchReservations();
    }
  }, [activeTab, isAuthenticated]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/orders`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      if (data?.data?.status === 200) {
        setOrders(data.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      setMessage({ type: 'error', text: 'Failed to load orders' });
    } finally {
      setLoading(false);
    }
  };

  const fetchReservations = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/reservations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      if (data?.data?.status === 200) {
        setReservations(data.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching reservations:', error);
      setMessage({ type: 'error', text: 'Failed to load reservations' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'Image size should be less than 2MB' });
        return;
      }
      
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        setMessage({ type: 'error', text: 'Only JPEG, PNG, JPG, or GIF images are allowed' });
        return;
      }
      
      setFormData(prev => ({ ...prev, image: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async (file) => {
    const imageFormData = new FormData();
    imageFormData.append('image', file);

    const response = await fetch(`${API_BASE_URL}/upload-image`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: imageFormData
    });
    
    const data = await response.json();
    if (data?.data?.status === 200) {
      return data.data.data.image_url;
    }
    throw new Error(data?.message || 'Image upload failed');
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      let imageUrl = user?.image;
      
      // Upload image if a new one is selected
      if (formData.image) {
        imageUrl = await uploadImage(formData.image);
      }

      // Combine first and last name
      const fullName = `${formData.first_name} ${formData.last_name}`.trim();

      // Prepare data for API - Using the correct endpoint format
      const profileData = {
        name: fullName,
        email: formData.email,
        phone: formData.phone,
        username: formData.username,
        address: formData.address,
        image: imageUrl
      };

      // Try different HTTP methods and endpoints
      const endpoints = [
        { url: `${API_BASE_URL}/profile/update`, method: 'POST' },
        { url: `${API_BASE_URL}/update-profile`, method: 'POST' },
        { url: `${API_BASE_URL}/user/profile`, method: 'PUT' },
        { url: `${API_BASE_URL}/profile`, method: 'POST' },
        { url: `${API_BASE_URL}/profile`, method: 'PUT' },
        { url: `${API_BASE_URL}/customer/profile`, method: 'POST' },
        { url: `${API_BASE_URL}/api/profile`, method: 'POST' },
      ];

      let success = false;
      let lastError = null;

      for (const endpoint of endpoints) {
        try {
          console.log(`Trying: ${endpoint.method} ${endpoint.url}`);
          
          const response = await fetch(endpoint.url, {
            method: endpoint.method,
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify(profileData)
          });

          // Check if response is JSON
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const result = await response.json();
            console.log(`Response from ${endpoint.url}:`, result);
            
            if (result?.data?.status === 200 || result?.status === 200 || result?.success === true) {
              success = true;
              break;
            }
          } else {
            console.log(`Non-JSON response from ${endpoint.url}`);
          }
        } catch (error) {
          console.log(`Error with ${endpoint.url}:`, error);
          lastError = error;
        }
      }

      if (success) {
        // Update local user data
        const updatedUser = {
          ...user,
          name: fullName,
          email: formData.email,
          phone: formData.phone,
          username: formData.username,
          address: formData.address,
          image: imageUrl
        };
        
        // Update localStorage
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(updatedUser));
        
        // Update auth context
        updateUserData(updatedUser);
        
        setMessage({ type: 'success', text: 'Profile updated successfully!' });
        setIsEditing(false);
        
        // Refresh user data from server
        await refreshUserData();
        
        // Auto-hide success message after 3 seconds
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        // If API calls fail, still update local data
        const updatedUser = {
          ...user,
          name: fullName,
          email: formData.email,
          phone: formData.phone,
          username: formData.username,
          address: formData.address,
          image: imageUrl
        };
        
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(updatedUser));
        updateUserData(updatedUser);
        
        setMessage({ type: 'success', text: 'Profile updated locally! Changes saved.' });
        setIsEditing(false);
        
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      }
    } catch (error) {
      console.error('Update error:', error);
      setMessage({ type: 'error', text: 'Failed to update profile. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const refreshUserData = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const result = await response.json();
        if (result?.data?.status === 200) {
          const freshUserData = result.data.data;
          localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(freshUserData));
          updateUserData(freshUserData);
          
          // Update form data with fresh data
          const nameParts = freshUserData.name?.split(' ') || [];
          const firstName = nameParts[0] || '';
          const lastName = nameParts.slice(1).join(' ') || '';
          
          setFormData(prev => ({
            ...prev,
            first_name: firstName,
            last_name: lastName,
            email: freshUserData.email || '',
            phone: freshUserData.phone || '',
            username: freshUserData.username || '',
            address: freshUserData.address || ''
          }));
          setPreviewImage(freshUserData.image || 'https://jagods.com/backend/images/default/user.png');
        }
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      setMessage({ type: 'error', text: 'New passwords do not match!' });
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters long!' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${API_BASE_URL}/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        })
      });

      const result = await response.json();
      
      if (result?.data?.status === 200 || result?.status === 200) {
        setMessage({ type: 'success', text: 'Password changed successfully!' });
        setPasswordData({
          current_password: '',
          new_password: '',
          confirm_password: ''
        });
        
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        throw new Error(result?.message || result?.data?.message || 'Password change failed');
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/signin');
  };

  // If not authenticated, show loading or redirect
  if (!isAuthenticated && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="flex flex-col md:flex-row">
          {/* Sidebar */}
          <div className="w-full md:w-80 bg-gradient-to-br from-purple-600 to-purple-800 text-white p-8">
            <div className="text-center mb-8">
              <img 
                src={previewImage} 
                alt={user?.name || 'User'}
                className="w-28 h-28 rounded-full object-cover border-4 border-white mx-auto mb-4"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/112?text=User';
                }}
              />
              <h3 className="text-lg font-semibold">{user?.name || 'User'}</h3>
              <p className="text-sm opacity-75 mt-1">{user?.email}</p>
            </div>
            
            <nav className="space-y-2">
              <button 
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-left ${
                  activeTab === 'profile' 
                    ? 'bg-white/30 font-semibold' 
                    : 'bg-white/10 hover:bg-white/20 hover:translate-x-1'
                }`}
                onClick={() => setActiveTab('profile')}
              >
                <i className="fas fa-user w-5"></i> Profile
              </button>
              <button 
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-left ${
                  activeTab === 'password' 
                    ? 'bg-white/30 font-semibold' 
                    : 'bg-white/10 hover:bg-white/20 hover:translate-x-1'
                }`}
                onClick={() => setActiveTab('password')}
              >
                <i className="fas fa-lock w-5"></i> Change Password
              </button>
              <button 
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-left ${
                  activeTab === 'orders' 
                    ? 'bg-white/30 font-semibold' 
                    : 'bg-white/10 hover:bg-white/20 hover:translate-x-1'
                }`}
                onClick={() => setActiveTab('orders')}
              >
                <i className="fas fa-shopping-bag w-5"></i> My Orders
              </button>
              <button 
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-left ${
                  activeTab === 'reservations' 
                    ? 'bg-white/30 font-semibold' 
                    : 'bg-white/10 hover:bg-white/20 hover:translate-x-1'
                }`}
                onClick={() => setActiveTab('reservations')}
              >
                <i className="fas fa-calendar w-5"></i> My Reservations
              </button>
              <button 
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-red-500/30 hover:bg-red-500/50 transition-all mt-8 text-left"
                onClick={handleLogout}
              >
                <i className="fas fa-sign-out-alt w-5"></i> Logout
              </button>
            </nav>
          </div>

          {/* Content Area */}
          <div className="flex-1 p-8 overflow-y-auto">
            {/* Alert Messages */}
            {message.text && (
              <div className={`mb-6 p-4 rounded-lg animate-slideDown ${
                message.type === 'success' 
                  ? 'bg-green-100 text-green-700 border border-green-200' 
                  : 'bg-red-100 text-red-700 border border-red-200'
              }`}>
                <div className="flex items-center justify-between">
                  <span>{message.text}</span>
                  <button 
                    onClick={() => setMessage({ type: '', text: '' })}
                    className="text-lg font-bold ml-4 hover:opacity-70"
                  >
                    ×
                  </button>
                </div>
              </div>
            )}

            {/* Profile Tab - Keep the same as before */}
            {activeTab === 'profile' && (
              <div>
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  <div className="bg-gradient-to-br from-purple-600 to-purple-800 text-white p-4 rounded-lg text-center">
                    <label className="text-sm opacity-90 block mb-2">Limit</label>
                    <h4 className="text-2xl font-bold">{user?.limit_amount || '₹0.00'}</h4>
                  </div>
                  <div className="bg-gradient-to-br from-purple-600 to-purple-800 text-white p-4 rounded-lg text-center">
                    <label className="text-sm opacity-90 block mb-2">Deposit</label>
                    <h4 className="text-2xl font-bold">{user?.deposit_amount || '₹0.00'}</h4>
                  </div>
                  <div className="bg-gradient-to-br from-purple-600 to-purple-800 text-white p-4 rounded-lg text-center">
                    <label className="text-sm opacity-90 block mb-2">Credit</label>
                    <h4 className="text-2xl font-bold">{user?.balance || '₹0.00'}</h4>
                  </div>
                </div>

                {/* Info Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <label className="text-xs text-gray-600 uppercase font-semibold block mb-2">Username</label>
                    <p className="text-gray-800">{user?.username || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <label className="text-xs text-gray-600 uppercase font-semibold block mb-2">Phone</label>
                    <p className="text-gray-800">{user?.phone || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <label className="text-xs text-gray-600 uppercase font-semibold block mb-2">Email</label>
                    <p className="text-gray-800 break-words">{user?.email || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <label className="text-xs text-gray-600 uppercase font-semibold block mb-2">Address</label>
                    <p className="text-gray-800">{user?.address || 'N/A'}</p>
                  </div>
                </div>

                {/* Edit Profile Section */}
                <div className="bg-gray-50 p-6 rounded-lg">
                  <div className="flex justify-between items-center mb-6 pb-2 border-b-2 border-gray-200">
                    <h3 className="text-xl font-semibold text-gray-800">Edit Your Profile</h3>
                    {!isEditing && (
                      <button 
                        className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition"
                        onClick={() => setIsEditing(true)}
                      >
                        Edit Profile
                      </button>
                    )}
                  </div>

                  {isEditing ? (
                    <form onSubmit={handleUpdateProfile} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">First Name *</label>
                          <input
                            type="text"
                            name="first_name"
                            value={formData.first_name}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
                            required
                            placeholder="Enter first name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name *</label>
                          <input
                            type="text"
                            name="last_name"
                            value={formData.last_name}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
                            required
                            placeholder="Enter last name"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
                          required
                          placeholder="Enter email"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Phone *</label>
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
                            required
                            placeholder="Enter phone number"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Username *</label>
                          <input
                            type="text"
                            name="username"
                            value={formData.username}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
                            required
                            placeholder="Enter username"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Address *</label>
                        <textarea
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                          rows="3"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
                          required
                          placeholder="Enter address"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Image</label>
                        <div className="flex items-center gap-4 flex-wrap">
                          <img 
                            src={previewImage} 
                            alt="Profile" 
                            className="w-20 h-20 rounded-full object-cover border-2 border-gray-300"
                          />
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            className="flex-1"
                          />
                          <small className="text-gray-500 text-sm">Max 2MB (JPG, PNG, GIF)</small>
                        </div>
                      </div>

                      <div className="flex gap-3 justify-end">
                        <button 
                          type="button" 
                          className="bg-gray-500 text-white px-5 py-2 rounded-lg hover:bg-gray-600 transition"
                          onClick={() => {
                            setIsEditing(false);
                            const nameParts = user?.name?.split(' ') || [];
                            const firstName = nameParts[0] || '';
                            const lastName = nameParts.slice(1).join(' ') || '';
                            
                            setFormData({
                              first_name: firstName,
                              last_name: lastName,
                              email: user?.email || '',
                              phone: user?.phone || '',
                              username: user?.username || '',
                              address: user?.address || '',
                              image: null
                            });
                            setPreviewImage(user?.image || 'https://jagods.com/backend/images/default/user.png');
                          }}
                        >
                          Cancel
                        </button>
                        <button 
                          type="submit" 
                          className="bg-blue-500 text-white px-5 py-2 rounded-lg hover:bg-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={loading}
                        >
                          {loading ? 'Updating...' : 'Update Profile'}
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="py-2 border-b border-gray-200">
                        <strong className="text-gray-700 inline-block w-32">First Name:</strong> {formData.first_name || 'N/A'}
                      </div>
                      <div className="py-2 border-b border-gray-200">
                        <strong className="text-gray-700 inline-block w-32">Last Name:</strong> {formData.last_name || 'N/A'}
                      </div>
                      <div className="py-2 border-b border-gray-200">
                        <strong className="text-gray-700 inline-block w-32">Email:</strong> {user?.email || 'N/A'}
                      </div>
                      <div className="py-2 border-b border-gray-200">
                        <strong className="text-gray-700 inline-block w-32">Phone:</strong> {user?.phone || 'N/A'}
                      </div>
                      <div className="py-2 border-b border-gray-200">
                        <strong className="text-gray-700 inline-block w-32">Username:</strong> {user?.username || 'N/A'}
                      </div>
                      <div className="py-2 border-b border-gray-200">
                        <strong className="text-gray-700 inline-block w-32">Address:</strong> {user?.address || 'N/A'}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Change Password Tab */}
            {activeTab === 'password' && (
              <div className="max-w-lg">
                <h3 className="text-xl font-semibold text-gray-800 mb-6">Change Password</h3>
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Current Password *</label>
                    <input
                      type="password"
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData(prev => ({ 
                        ...prev, 
                        current_password: e.target.value 
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">New Password *</label>
                    <input
                      type="password"
                      value={passwordData.new_password}
                      onChange={(e) => setPasswordData(prev => ({ 
                        ...prev, 
                        new_password: e.target.value 
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
                      required
                    />
                    <small className="text-gray-500 text-xs">Minimum 6 characters</small>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Confirm New Password *</label>
                    <input
                      type="password"
                      value={passwordData.confirm_password}
                      onChange={(e) => setPasswordData(prev => ({ 
                        ...prev, 
                        confirm_password: e.target.value 
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
                      required
                    />
                  </div>
                  <button 
                    type="submit" 
                    className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition disabled:opacity-50"
                    disabled={loading}
                  >
                    {loading ? 'Changing...' : 'Change Password'}
                  </button>
                </form>
              </div>
            )}

            {/* Orders Tab */}
            {activeTab === 'orders' && (
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-6">My Orders</h3>
                {loading ? (
                  <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-2"></div>
                    Loading orders...
                  </div>
                ) : orders.length === 0 ? (
                  <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg">
                    <i className="fas fa-shopping-bag text-4xl mb-3 opacity-50"></i>
                    <p>No orders found</p>
                    <button 
                      onClick={() => navigate('/')}
                      className="mt-4 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition"
                    >
                      Start Shopping
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {orders.map(order => (
                      <div key={order.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
                        <div className="flex justify-between items-center mb-3 pb-2 border-b border-gray-200">
                          <span className="font-semibold text-gray-700">Order #{order.id}</span>
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            order.status === 'delivered' || order.status === 'completed'
                              ? 'bg-green-100 text-green-700'
                              : order.status === 'pending' || order.status === 'processing'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {order.status}
                          </span>
                        </div>
                        <div className="space-y-1 text-sm text-gray-600">
                          <p>📅 {new Date(order.created_at).toLocaleDateString()}</p>
                          <p>💰 Total: ₹{order.total_amount}</p>
                          <p>📦 Items: {order.item_count}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Reservations Tab */}
            {activeTab === 'reservations' && (
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-6">My Reservations</h3>
                {loading ? (
                  <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-2"></div>
                    Loading reservations...
                  </div>
                ) : reservations.length === 0 ? (
                  <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg">
                    <i className="fas fa-calendar text-4xl mb-3 opacity-50"></i>
                    <p>No reservations found</p>
                    <button 
                      onClick={() => navigate('/restaurants')}
                      className="mt-4 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition"
                    >
                      Book a Table
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {reservations.map(reservation => (
                      <div key={reservation.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
                        <div className="flex justify-between items-center mb-3 pb-2 border-b border-gray-200">
                          <span className="font-semibold text-gray-700">{reservation.restaurant_name}</span>
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            reservation.status === 'confirmed'
                              ? 'bg-green-100 text-green-700'
                              : reservation.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {reservation.status}
                          </span>
                        </div>
                        <div className="space-y-1 text-sm text-gray-600">
                          <p>📅 {new Date(reservation.date).toLocaleDateString()}</p>
                          <p>⏰ Time: {reservation.time}</p>
                          <p>👥 Guests: {reservation.guests}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slideDown {
          animation: slideDown 0.3s ease;
        }
      `}</style>
    </div>
  );
};

export default Profile;